import autoAnimate from '@formkit/auto-animate'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '../../hooks/useApi'
import { useCoffinEditor, type Model } from '../../hooks/useCoffinEditor'
import { useDesignCrud } from '../../hooks/useDesignCrud'
import { ChevronLeft } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '../ui/button'
import { Card } from './card'
import { CoffinReadMoreModal } from './coffin-read-more-modal'

const ORBIT_DESCRIPTION = `Orbit kisten er Danmarks mest bæredygtige kiste. Materialet består af 80% mindre træ end en normal trækiste. Når der produceres 1 trækiste, kan der produceres 5 Orbit kister. Orbit er 100% fri for maling, metal, lak og kemi og vejer kun 12 kg.`

const ORIBT_MORE_DESCRIPTION = `Kistens vægt: 12 kg
Højde: 41cm
Længde: 208cm
Bredde: 65cm

Alle vores alm. størrelse kister kan tage op til: 120kg`

const FLEX_DESCRIPTION = `Flexkisten er både en begravelses- og kremeringskiste.
              
Alle vores Flexkister er af spån og MDF, hvorpå der er lagt papir. Dette gør, at vi kun skal lakere vores kister én gang, hvilket er et stort hensyn til miljøet.`

const FLEX_MORE_DESCRIPTION = `Kistens Vægt: 45 kg
Højde: 49cm
Længde: 201cm
Bredde: 63cm

Alle vores alm. størrelse kister kan tage op til: 120kg`

const LIGB_DESCRIPTION = `Alle vores kremeringskister er af spån og MDF, hvorpå der er lagt papir. Dette gør, at vi kun skal lakere vores kister én gang, hvilket er et stort hensyn til miljøet.`

const LIGB_MORE_DESCRIPTION = `Kistens Vægt: 42 kg
Højde: 44cm
Længde: 201cm
Bredde: 63cm

Alle vores alm. størrelse kister kan tage op til: 120kg`

const baseUrl = 'https://cdesigner.nembedemand.dk/static' ?? ''

export const CoffinModel = (p: { close: () => void }) => {
  const { model, activeDesignId } = useCoffinEditor()
  const { getDesign } = useDesignCrud()
  const [activePreview, setActivePreview] = useState<Model | null>(null)
  const { getSuggestions } = useApi()
  const [loadingDesign, setLoadingDesign] = useState<string | null>(null)
  const parent = useRef(null)

  useEffect(() => {
    parent.current && autoAnimate(parent.current)
  }, [parent])

  const { data: suggestions } = useQuery({
    queryKey: ['suggestions'],
    queryFn: getSuggestions,
  })

  const SuggestionButton = ({
    title,
    readMoreDescription,
  }: {
    title: string
    readMoreDescription: string
  }) => {
    const [readMoreIsOpen, setReadMoreIsOpen] = useState(false)

    return (
      <div className='items flex flex-grow flex-col items-end justify-end gap-2 p-2 md:flex-row'>
        <CoffinReadMoreModal
          isOpen={readMoreIsOpen}
          setIsOpen={setReadMoreIsOpen}
          title={title}
          description={readMoreDescription}
        >
          <Button
            variant='outline'
            className='w-full md:w-auto'
            onClick={(e) => {
              e.stopPropagation()
              setReadMoreIsOpen(true)
            }}
          >
            Læs mere
          </Button>
        </CoffinReadMoreModal>
      </div>
    )
  }

  const modelNames = {
    orbit: 'Orbit',
    flex: 'Flexkiste',
    ligb: 'Kremeringskiste',
  }

  return (
    <div className='p-3' ref={parent}>
      {!activePreview && (
        <div className='grid grid-cols-2 gap-2 lg:grid-cols-3'>
          <Card
            imageUrl={`${baseUrl}/orbit.png`}
            title={modelNames.orbit}
            isActive={model === 'orbit'}
            onClick={() => {
              setActivePreview('orbit')
            }}
            description={ORBIT_DESCRIPTION}
            footer={
              <SuggestionButton
                title='Orbit'
                readMoreDescription={ORIBT_MORE_DESCRIPTION}
              />
            }
            hideTextOnSmallScreens
          />
          <Card
            imageUrl={`${baseUrl}/flex.png`}
            title={modelNames.flex}
            isActive={model === 'flex'}
            onClick={() => {
              setActivePreview('flex')
            }}
            description={FLEX_DESCRIPTION}
            footer={
              <SuggestionButton
                title='Flexkiste'
                readMoreDescription={FLEX_MORE_DESCRIPTION}
              />
            }
            hideTextOnSmallScreens
          />
          <Card
            imageUrl={`${baseUrl}/ligb.png`}
            title={modelNames.ligb}
            isActive={model === 'ligb'}
            onClick={() => {
              setActivePreview('ligb')
            }}
            description={LIGB_DESCRIPTION}
            footer={
              <SuggestionButton
                title='Kremeringskiste'
                readMoreDescription={LIGB_MORE_DESCRIPTION}
              />
            }
            hideTextOnSmallScreens
          />
        </div>
      )}

      {activePreview && (
        <>
        <div className="flex items-center mb-3">
          <Button
            className='rounded-full mr-2'
            onClick={() => setActivePreview(null)}
          >
            <ChevronLeft className='h-4 w-4' />
          </Button>
          { modelNames[activePreview] }
        </div>
          
          <div className='grid grid-cols-2 gap-2 lg:grid-cols-3 max-h-[calc(85vh-220px)] overflow-auto'>
            {suggestions?.results
              .filter((s) => s.type === activePreview)
              .map((suggestion, i) => (
                <Card
                  key={i}
                  imageUrl={suggestion.previewImageUrl}
                  title={suggestion.title}
                  isLoading={loadingDesign === suggestion.designId}
                  isActive={activeDesignId === suggestion.designId}
                  footer={
                    <Button
                      variant='outline'
                      className='m-2'
                      onClick={async () => {
                        setLoadingDesign(suggestion.designId)
                        try {
                          await getDesign(suggestion.designId, true)
                        } catch (e) {
                          toast.error('Der opstod en fejl. Prøv venligst igen')
                        }
                        setLoadingDesign(null)
                        p.close()
                      }}
                    >
                      Vælg udgave
                    </Button>
                  }
                />
              ))}
          </div>
        </>
      )}
    </div>
  )
}
